import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { tagStroke, truncateLabel } from '../../utils/neuralSceneUtils';
import {
  applyNodeStatusLook,
  createNodeHalo,
  createNodeMaterial,
  nodeColor,
  nodeEmissiveIntensity,
  nodeOpacity,
} from '../neuralMaterials';
import { liveLabelGap } from '../runNeuralAnimationFrame/labelSlots';
import { NODE_CORE_RADIUS, NODE_DISK, layoutScaleOf, wantsHalo } from './constants';

export function createNodeController({
  nodeGroup,
  meshByTaskId,
  haloByTaskId,
  labelByTaskId,
  raycastTargets,
}) {
  const removeHalo = (taskId) => {
    const h = haloByTaskId.get(taskId);
    if (!h) return;
    nodeGroup.remove(h.halo);
    h.halo.material.dispose();
    haloByTaskId.delete(taskId);
  };

  const detachLabelForTask = (taskId, labelRec) => {
    if (!labelRec?.label) return;
    if (labelRec.label.parent) nodeGroup.remove(labelRec.label);
    labelByTaskId.delete(taskId);
  };

  const mountLabel = (labelRec, on) => {
    if (!labelRec?.label) return;
    if (on && !labelRec.label.parent) nodeGroup.add(labelRec.label);
    else if (!on && labelRec.label.parent) nodeGroup.remove(labelRec.label);
  };

  function disposeMissionMesh(mesh) {
    nodeGroup.remove(mesh);
    if (mesh.geometry !== NODE_DISK) mesh.geometry?.dispose?.();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose?.());
  }

  /** Follow the last clear seat, or hang under the disk on first show. */
  const placeLabelNearMesh = (mesh, label) => {
    const off = label.userData;
    if (off.wx == null) {
      label.position.copy(mesh.position);
      label.position.y -= NODE_CORE_RADIUS * layoutScaleOf(mesh) + liveLabelGap(mesh);
      label.center.set(0.5, 0);
      return;
    }
    label.position.set(mesh.position.x + off.wx, mesh.position.y + off.wy, mesh.position.z + off.wz);
  };

  function createMeshesForNode(node) {
    const finalColor = nodeColor(node);

    const mat = createNodeMaterial(node);
    const mesh = new THREE.Mesh(NODE_DISK, mat);
    mesh.renderOrder = 2;
    mesh.userData.sharedGeom = true;
    mesh.matrixAutoUpdate = false;
    mesh.frustumCulled = false;
    mesh.position.set(...node.pos);
    mesh.visible = false;
    // Stays out of the scene. The shared instanced mesh is what gets drawn.
    mesh.userData = {
      taskId: node.id,
      nodeMeta: node,
      baseColor: finalColor.clone(),
      baseOpacity: nodeOpacity(node),
      exiting: false,
      visT: 0,
      hoverLerp: 0,
      layoutScale: node.size ?? 1,
      homePos: new THREE.Vector3(...node.pos),
      revealX: 0,
      revealY: 0,
    };

    const tagEl = document.createElement('div');
    tagEl.className = `neural-view__node-tag${node.parentTaskId ? ' neural-view__node-tag--satellite' : ''}${node.hasNest ? ' neural-view__node-tag--hub' : ''}`;
    tagEl.dataset.taskId = node.id;
    tagEl.textContent = truncateLabel(node.name, 56);
    const stroke = tagStroke(finalColor, 0.58);
    tagEl.style.borderColor = stroke;
    tagEl.style.opacity = '0';
    const label = new CSS2DObject(tagEl);
    label.center.set(0.5, 0);
    placeLabelNearMesh(mesh, label);
    // Stay off the scene until the label pass actually shows this tag —
    // CSS2DRenderer otherwise projects every node every frame.
    labelByTaskId.set(node.id, { label, element: tagEl });

    if (wantsHalo(node)) {
      const halo = createNodeHalo(node);
      halo.position.copy(mesh.position);
      halo.position.z -= 0.05;
      halo.matrixAutoUpdate = false;
      halo.userData = { taskId: node.id, haloFor: mesh };
      halo.visible = false;
      const working = node.statusCategory === 'in_progress';
      haloByTaskId.set(node.id, {
        halo,
        mesh,
        pulse: working,
        keep: true,
        glowLive: 1,
        glowWork: working ? 1 : 0,
        glowInner: node.isAvailable ? 1 : 0,
      });
    }

    return mesh;
  }

  function reconcileNodePresentation(mesh, node) {
    const finalColor = nodeColor(node);
    mesh.userData.nodeMeta = node;
    mesh.userData.baseColor = finalColor.clone();
    mesh.userData.baseOpacity = nodeOpacity(node);
    applyNodeStatusLook(mesh.material, node);
    if (mesh.material.emissive) {
      mesh.material.emissive.copy(finalColor);
      mesh.material.emissiveIntensity = nodeEmissiveIntensity(node);
    }

    if (!mesh.userData.exiting) {
      mesh.material.color.copy(finalColor);
    }

    const existing = haloByTaskId.get(node.id);
    const working = node.statusCategory === 'in_progress';
    if (wantsHalo(node) && !existing && !mesh.userData.exiting) {
      const halo = createNodeHalo(node);
      halo.position.copy(mesh.position);
      halo.position.z -= 0.05;
      halo.matrixAutoUpdate = false;
      halo.userData = { taskId: node.id, haloFor: mesh };
      halo.visible = false;
      const born = (mesh.userData.visT ?? 0) < 0.08;
      haloByTaskId.set(node.id, {
        halo,
        mesh,
        pulse: working,
        keep: true,
        glowLive: born ? 1 : 0,
        glowWork: born && working ? 1 : 0,
        glowInner: born && node.isAvailable ? 1 : 0,
      });
    } else if (existing && !wantsHalo(node)) {
      existing.pulse = false;
      existing.keep = false;
      existing.halo.material.color.copy(finalColor);
    } else if (existing) {
      existing.pulse = working;
      existing.keep = true;
      existing.halo.material.color.copy(finalColor);
    }

    const lab = labelByTaskId.get(node.id);
    if (lab?.element) {
      lab.element.dataset.taskId = node.id;
      lab.element.textContent = truncateLabel(node.name, 56);
      const stroke = tagStroke(finalColor, 0.58);
      lab.element.style.borderColor = stroke;
    }
  }

  function rebuildRaycastTargets() {
    raycastTargets.length = 0;
    meshByTaskId.forEach((m) => {
      const { exiting } = m.userData;
      if (exiting || !m.visible) return;
      raycastTargets.push(m);
      // Soft halos overlap at globe scale; keep picking on the solid cores.
    });
  }

  return {
    createMeshesForNode,
    reconcileNodePresentation,
    rebuildRaycastTargets,
    placeLabelNearMesh,
    mountLabel,
    removeHalo,
    detachLabelForTask,
    disposeMissionMesh,
  };
}
